import React, {Component} from 'react';
import * as d3 from 'd3';
import moment from 'moment';
import durationSetup from 'moment-duration-format';
import Immutable, {List, Map} from 'immutable';

durationSetup(moment);

function humanDuration(duration) {
  if (duration < 10000) {
    return `${duration}ms`;
  }

  const seconds = duration / 1000.0;

  // 5 minutes
  if (duration < 1000 * 60 * 5) {
    // For longer than 10 seconds, show in seconds
    return `${seconds.toFixed(1)}s`;
  }

  return moment.duration(seconds, 'seconds').format();
}


export default class Timeline extends Component {
  constructor(props) {
    super(props);

    this.tasks = Immutable.fromJS(props.tasks).sortBy(task => task.get('startMillis'));
    this.taskGroups = this.tasks.groupBy(task => task.get('taskName'));

    this.events = Immutable.fromJS(props.events);
    this.bands = this.bandsFromTasks(this.tasks);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.tasks !== this.props.tasks) {
      this.tasks = Immutable.fromJS(nextProps.tasks);
      this.bands = this.bandsFromTasks(this.tasks);
    }

    if (nextProps.events !== this.props.events) {
      this.events = Immutable.fromJS(nextProps.events);
    }
  }

  startMillis() {
    return this.tasks.map(task => task.get('startMillis')).min();
  }

  endMillis() {
    return this.tasks.map(task => task.get('endMillis')).max();
  }

  /**
   * Go from our list of task instances to a data structure that is better to work
   * with internally. We can have multiple bands within a single task if the timings
   * overlap, so we need to split these up.
   *
   * Returns a list of bands where each band is an Immutable map of:
   * {
   *   taskName: String,
   *   showLabel: Boolean,
   *   instances: List[TaskInstance],
   *   taskIndex: Int,
   *   bandIndex: Int
   * }
   */
  bandsFromTasks() {
    return this.taskGroups
      .mapEntries((entry, taskIndex) => {
        const [taskName, tasks] = entry;
        let bands = List()

        // Couldn't really figure out a functional way of doing this
        tasks
          .sortBy(task => task.get('startMillis'))
          .forEach((task) => {
            if (bands.isEmpty()) {
              bands = bands.push(List([task]));
            } else {
              const bandIndex = bands.findIndex((band) => {
                return task.get('startMillis') > band.get(-1).get('endMillis');
              });

              if (bandIndex !== -1) {
                // We can add ourselves to this band
                bands = bands.update(bandIndex, band => band.push(task));
              } else {
                // There are no bands that don't have colliding tasks, make a new one
                bands = bands.push(List([task]));
              }
            }
          });

        // Normalise some information onto each band that is useful when drawing our timeline
        return [taskName, bands.map((band, index) => {
          return Map({
            taskName,
            taskIndex,
            showLabel: index === 0,
            tasks: band
          });
        })];
      })
      .valueSeq() // Unwrap {taskName: band}
      .flatten(1) // Flatten list of arrays to a single array of bands
      .map((band, bandIndex) => {
        // When we filter by bands that show labels, it's useful to know what index they are on
        return band.set('bandIndex', bandIndex);
      });

  }

  componentDidMount() {
    d3.select(this.rootNode).style('position', 'relative');

    const svg = d3
      .select(this.rootNode)
      .append('svg');

    const startMillis = this.startMillis();
    const endMillis = this.endMillis();

    const duration = endMillis - startMillis;

    const width = svg.node().parentNode.offsetWidth;

    const contentHeight = this.props.bandHeight * this.bands.count();
    const height = contentHeight + this.props.topMargin + this.props.bottomMargin;

    const tooltip = d3.select(this.rootNode).append('div')
      .attr('class', 'timeline-tooltip')
      .style('position', 'absolute')
      .style('opacity', 0)
      .style('pointer-events', 'none')
      .style('background-color', 'white')
      .style('padding', '10px')
      .style('border-color', this.props.axisColor)
      .style('border-width', '1px')
      .style('border-style', 'solid')

    function showTooltip(content, left, top) {
      tooltip.transition().duration(50).style('opacity', 1);
      tooltip
        .html(content)
        .style('left', left + 'px')
        .style('top', top + 'px')
    }

    svg.attr('width', width).attr('height', height);

    const xScale = d3
      .scaleLinear()
      .domain([0, endMillis - startMillis])
      .range([0, width - this.props.leftMargin - this.props.rightMargin])
      .clamp(true);

    const xAxis = d3.axisTop(xScale)
      .ticks(10)
      .tickFormat((v, index) => {
        if (index === 0 || !this.props.shouldShowRelativeTime) {
          // Show full timestamp for first and last tick
          return moment(v + startMillis).format(this.props.timeFormat);
        }

        if (duration < 10000) {
          return `+${v}ms`;
        }

        // 5 minutes
        if (duration < 1000 * 60 * 5) {
          // For longer than 10 seconds, show in seconds
          const seconds = v / 1000.0;
          return `+${seconds.toFixed(1)}s`;
        }

        // Anything longer, user our timeFormat
        return moment(v + startMillis).format(this.props.timeFormat);
      });

    const yScale = d3
      .scaleBand()
      .range([this.props.topMargin, height - this.props.bottomMargin])
      .paddingInner(this.props.bandPaddingInner)
      .paddingOuter(this.props.bandPaddingOuter)
      .domain(Immutable.Range(0, this.bands.count()).toJS());

    const bandTicks = this.bands.filter(band => band.get('showLabel')).map(band => band.get('bandIndex')).toJS();

    const yAxis = d3
      .axisLeft(yScale)
      .tickValues(bandTicks)
      .tickFormat((index) => {
        return this.bands.get(index).get('taskName');
      });

    svg
      .append('g')
      .attr('class', 'y-axis')
      .attr('color', this.props.axisColor)
      .attr('transform', `translate(${this.props.leftMargin - 2}, 0)`)
      .attr('shape-rendering', 'crispEdges')
      .call(yAxis);

    svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('color', this.props.axisColor)
      .attr('transform', `translate(${this.props.leftMargin - 2}, ${this.props.topMargin})`)
      .attr('shape-rendering', 'crispEdges')
      .call(xAxis);

    const timeline = this;

    this.bands.forEach((band, bandIndex) => {
      const taskIndex = band.get('taskIndex');

      const taskBars = svg
        .selectAll('.bar')
        .data(band.get('tasks').toJS())
        .enter()
        .append('g');

      const c = this.props.colors[taskIndex % this.props.colors.length];

      taskBars
        .append('rect')
        .attr('class', 'timeline-task')
        .attr('y', () => {
          // In order to get single line borders we have to offset by half a pixel
          return Math.round(yScale(bandIndex)) + 0.5;
        })
        .attr('x', (task) => {
          // I seem to need to offset by 2px to get it to not overlap the axis
          return Math.round(xScale(task.startMillis - startMillis) + this.props.leftMargin);
        })
        .attr('width', (task) => {
          return Math.round(Math.max(1, xScale(task.endMillis - task.startMillis)));
        })
        .attr('height', Math.round(yScale.bandwidth()))
        .attr('shape-rendering', 'crispEdges')
        .attr('stroke', c)
        .attr('stroke-width', 1)
        .attr('stroke-opacity', this.props.strokeOpacity)
        .attr('fill', c)
        .attr('fill-opacity', this.props.fillOpacity)
        .on('mouseover', function (data) {
          d3.select(d3.event.target)
            .attr('fill-opacity', timeline.props.fillHoverOpacity)
            .attr('stroke-opacity', timeline.props.strokeHoverOpacity);

          const metadata = timeline.props.metadataRenderer(data.metadata);
          showTooltip(timeline.props.bandTooltipRenderer(band, data, metadata), d3.event.pageX, yScale(bandIndex) + yScale.bandwidth() / 2);
        })
        .on('mousemove', (data) => {
          // Track horizontally
          tooltip.style('left', d3.event.pageX + 'px');
        })
        .on('mouseout', function () {
          tooltip.transition().duration(50).style('opacity', 0);

          d3.select(this)
            .attr('fill-opacity', timeline.props.fillOpacity)
            .attr('stroke-opacity', timeline.props.strokeOpacity);
        });
    });

    const events = svg
      .selectAll('.event')
      .data(this.events.toJS())
      .enter()
      .append('g');

    function eventX(event) {
      return Math.round(xScale(event.atMillis - startMillis) + timeline.props.leftMargin);
    }

    events
      .append('line')
      .attr('class', 'timeline-event')
      .attr('class', event => `timeline-event-${event.type || 'info'}`)
      .attr('x1', event => eventX(event))
      .attr('x2', event => eventX(event))
      .attr('y1', Math.round(this.props.topMargin + this.props.eventRadius))
      .attr('y2', Math.round(height - this.props.bottomMargin))
      .attr('shape-rendering', 'crispEdges')
      .attr('stroke-opacity', this.props.strokeOpacity)
      .attr('stroke', event => timeline.props.eventColor(event))
      .attr('stroke-width', this.props.eventWidth)

    // Hitslop for our line
    events
      .append('line')
      .attr('class', 'timeline-event-hitbox')
      .attr('x1', event => eventX(event))
      .attr('x2', event => eventX(event))
      .attr('y1', Math.round(this.props.topMargin + this.props.eventRadius))
      .attr('y2', Math.round(height - this.props.bottomMargin))
      .attr('stroke', 'transparent')
      .attr('stroke-width', this.props.eventWidth + this.props.eventHitSlop)

    events
      .append('circle')
      .attr('cx', event => xScale(event.atMillis - startMillis) + this.props.leftMargin)
      .attr('cy', () => this.props.topMargin + this.props.eventRadius)
      .attr('r', this.props.eventRadius)
      .attr('stroke', event => timeline.props.eventColor(event))
      .attr('fill', event => timeline.props.eventColor(event))
      .attr('shape-rendering', 'geometricPrecision')

    events
      .on('mouseover', (data) => {
        d3.select(d3.event.target)
          .attr('fill-opacity', timeline.props.fillHoverOpacity)
          .attr('stroke-opacity', timeline.props.strokeHoverOpacity);

        const metadata = timeline.props.metadataRenderer(data.metadata);
        showTooltip(timeline.props.eventTooltipRenderer(data, metadata), d3.event.pageX, d3.event.pageY);
      })
      .on('mousemove', () => {
        // Track horizontally
        tooltip.style('top', `${d3.event.pageY}px`);
      })
      .on('mouseout', function () {
        tooltip.transition().duration(50).style('opacity', 0);

        d3.select(d3.event.target)
          .attr('fill-opacity', timeline.props.fillOpacity)
          .attr('stroke-opacity', timeline.props.strokeOpacity);
      });
  }

  eventColor(event) {
    return event.color || this.props.defaultEventColor;
  }

  render() {
    return (
      <div>
        { this.props.title && <div className='timeline-title'>{this.props.title}</div> }
        <div
          className='timeline-container'
          ref={(rootNode) => {
            this.rootNode = rootNode;
          }}
          style={{
            borderColor: '#e5e5e5',
            borderWidth: 1,
            borderStyle: 'solid'
          }}
        />
      </div>
    );
  }
}

Timeline.defaultProps = {
  timeFormat: 'HH:mm:ss.SSS',
  axisColor: '#96A3B6',
  leftMargin: 150,
  bottomMargin: 50,
  rightMargin: 20,
  topMargin: 35,
  bandHeight: 40,
  bandPaddingInner: 0.2,
  bandPaddingOuter: 0.2,
  fillOpacity: 0.3,
  fillHoverOpacity: 0.6,
  strokeOpacity: 0.6,
  strokeHoverOpacity: 1,
  shouldShowRelativeTime: true,
  eventWidth: 2,
  eventHitSlop: 4,
  eventRadius: 3,
  defaultEventColor: 'c0392b',
  colors: [
    '#1abc9c',
    '#f1c40f',
    '#3498db',
    '#e74c3c',
    '#9b59b6',
    '#34495e',
    '#2ecc71',
    '#e67e22',
    '#95a5a6',
    '#16a085',
    '#f39c12',
    '#2980b9',
    '#c0392b',
    '#8e44ad',
    '#bdc3c7',
    '#27ae60',
    '#d35400',
    '#2c3e50'
  ],
  metadataRenderer: (metadata) => {
    console.log('metadata', metadata);
    return Immutable.fromJS(metadata || {})
      .map((value, key) => {
        return `<span class='key'>${key}</span>&nbsp;<span class='value'>${value}</span>`;
      })
      .valueSeq()
      .join('<br />\n');
  },
  bandTooltipRenderer: (band, task, metadata) => {
    console.log('band', metadata);
    return `
      <div class='title'>${band.get('taskName')}</div>
      <div class='detail'>
        <span class='key'>Duration:</span>
        <span class='value'>${humanDuration(task.endMillis - task.startMillis)}</span>
      </div>
      <div class='metadata'>${metadata}</div>
    `;
  },
  eventTooltipRenderer: (event, metadata) => {
    return `
      <div class='title'>${event.title}</div>
      <div class='description'>${event.description || ''}</div>
      <div class='metadata'>${metadata}</div>
    `;
  },
  eventColor: function(event) {
    const t = event.eventType || 'info';
    if (t === 'success') {
      return '#2ecc71';
    }

    if (t === 'warn') {
      return '#d35400';
    }

    if (t === 'error') {
      return '#c0392b';
    }

    return '#2980b9';
  }
};
